import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Apple, Clock, Plus, X, Edit, Check, Calendar, AlertTriangle, Activity } from 'lucide-react';

interface NutritionSectionProps {
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
}

export const NutritionSection = ({ data, onChange }: NutritionSectionProps) => {
  const [editingProduct, setEditingProduct] = useState<number | null>(null);
  const [editingMeal, setEditingMeal] = useState<number | null>(null);

  const handleChange = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const dietTypes = [
    'Crocchette', 'Umido', 'BARF', 'Casalingo', 'Misto', 'Terapeutico'
  ];

  const addMeal = () => {
    const schedule = data.feeding_schedule || [];
    const newIndex = schedule.length;
    schedule.push({
      time: '',
      notes: '',
      date: '',
      quantity: '',
      food_type: '',
      recurring: true,
      frequency: 'daily',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      weeks: [],
      months: [],
      custom_dates: []
    });
    handleChange('feeding_schedule', schedule);
    setEditingMeal(newIndex);
  };

  const updateMeal = (index: number, field: string, value: string | boolean | string[]) => {
    const schedule = [...(data.feeding_schedule || [])];
    schedule[index] = { ...schedule[index], [field]: value };
    handleChange('feeding_schedule', schedule);
  };

  const removeMeal = (index: number) => {
    const schedule = data.feeding_schedule || [];
    schedule.splice(index, 1);
    handleChange('feeding_schedule', schedule);
    setEditingMeal(null);
  };

  const saveMeal = (index: number) => {
    setEditingMeal(null);
  };

  const addProduct = () => {
    const products = data.main_products || [];
    const newIndex = products.length;
    products.push({
      brand: '',
      product_name: '',
      protein_source: '',
      start_date: '',
      end_date: '',
      expiry_date: '',
      purchase_date: '',
      notes: ''
    });
    handleChange('main_products', products);
    setEditingProduct(newIndex);
  };

  const updateProduct = (index: number, field: string, value: string) => {
    const products = [...(data.main_products || [])];
    products[index] = { ...products[index], [field]: value };
    handleChange('main_products', products);
  };

  const removeProduct = (index: number) => {
    const products = data.main_products || [];
    products.splice(index, 1);
    handleChange('main_products', products);
    setEditingProduct(null);
  };

  const saveProduct = (index: number) => {
    setEditingProduct(null);
  };

  // Helper function to check if product is expiring soon
  const isExpiringSoon = (expiryDate: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    return expiry < today;
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Diet Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Apple className="w-5 h-5 text-green-500" />
            Informazioni Dietetiche
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="diet_type">Tipo di Dieta</Label>
              <Select value={data.diet_type || ''} onValueChange={(value) => handleChange('diet_type', value)}>
                <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                  <SelectValue placeholder="Seleziona tipo dieta" />
                </SelectTrigger>
                <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                  {dietTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="diet_start_date">Inizio Dieta Attuale</Label>
              <Input
                id="diet_start_date"
                type="date"
                value={data.diet_start_date || ''}
                onChange={(e) => handleChange('diet_start_date', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="daily_calories">Calorie Giornaliere</Label>
              <Input
                id="daily_calories"
                type="number"
                value={data.daily_calories || ''}
                onChange={(e) => handleChange('daily_calories', e.target.value)}
                placeholder="kcal/giorno"
              />
            </div>
            <div>
              <Label htmlFor="meals_per_day">Pasti al Giorno</Label>
              <Input
                id="meals_per_day"
                type="number"
                value={data.meals_per_day || ''}
                onChange={(e) => handleChange('meals_per_day', e.target.value)}
                placeholder="Numero pasti"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="dietary_restrictions">Restrizioni Alimentari</Label>
            <Textarea
              id="dietary_restrictions"
              value={data.dietary_restrictions || ''}
              onChange={(e) => handleChange('dietary_restrictions', e.target.value)}
              placeholder="Allergie, intolleranze, alimenti vietati..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Main Products */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Apple className="w-5 h-5 text-green-500" />
              Prodotti Alimentari
            </div>
            <Button onClick={addProduct} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Prodotto
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.main_products && data.main_products.length > 0 ? (
            <div className="space-y-4">
              {data.main_products.map((product: any, index: number) => {
                const isEditing = editingProduct === index;
                const expiring = isExpiringSoon(product.expiry_date);
                const expired = isExpired(product.expiry_date);
                
                return (
                  <div key={index} className="border rounded-lg p-4">
                    {isEditing ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Marca *</Label>
                            <Input
                              value={product.brand || ''}
                              onChange={(e) => updateProduct(index, 'brand', e.target.value)}
                              placeholder="Nome marca"
                            />
                          </div>
                          <div>
                            <Label>Prodotto *</Label>
                            <Input
                              value={product.product_name || ''}
                              onChange={(e) => updateProduct(index, 'product_name', e.target.value)}
                              placeholder="Nome prodotto"
                            />
                          </div>
                          <div>
                            <Label>Fonte Proteica</Label>
                            <Input
                              value={product.protein_source || ''}
                              onChange={(e) => updateProduct(index, 'protein_source', e.target.value)}
                              placeholder="es. Pollo, Salmone"
                            />
                          </div>
                          <div>
                            <Label>Data Acquisto</Label>
                            <Input
                              type="date"
                              value={product.purchase_date || ''}
                              onChange={(e) => updateProduct(index, 'purchase_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Inizio Utilizzo</Label>
                            <Input
                              type="date"
                              value={product.start_date || ''}
                              onChange={(e) => updateProduct(index, 'start_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Fine Utilizzo</Label>
                            <Input
                              type="date"
                              value={product.end_date || ''}
                              onChange={(e) => updateProduct(index, 'end_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Data Scadenza</Label>
                            <Input
                              type="date"
                              value={product.expiry_date || ''}
                              onChange={(e) => updateProduct(index, 'expiry_date', e.target.value)}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label>Note</Label>
                            <Textarea
                              value={product.notes || ''}
                              onChange={(e) => updateProduct(index, 'notes', e.target.value)}
                              placeholder="Note sul prodotto, dosaggio, preferenze..."
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => saveProduct(index)} size="sm">
                            <Check className="w-4 h-4 mr-2" />
                            Salva
                          </Button>
                          <Button variant="outline" onClick={() => removeProduct(index)} size="sm">
                            <X className="w-4 h-4 mr-2" />
                            Rimuovi
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium">{product.brand} - {product.product_name || 'Prodotto'}</h4>
                            {product.expiry_date && (
                              <Badge variant={expired ? 'destructive' : expiring ? 'default' : 'secondary'}>
                                {expired && <AlertTriangle className="w-3 h-3 mr-1" />}
                                <Calendar className="w-3 h-3 mr-1" />
                                Scade: {new Date(product.expiry_date).toLocaleDateString('it-IT')}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            {product.protein_source && <p>Fonte proteica: {product.protein_source}</p>}
                            {product.start_date && product.end_date && (
                              <p>Periodo utilizzo: {new Date(product.start_date).toLocaleDateString('it-IT')} - {new Date(product.end_date).toLocaleDateString('it-IT')}</p>
                            )}
                            {product.notes && <p>{product.notes}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setEditingProduct(index)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeProduct(index)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Nessun prodotto registrato. Clicca "Aggiungi Prodotto" per iniziare.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Feeding Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              Programma Alimentazione
            </div>
            <Button onClick={addMeal} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Pasto
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.feeding_schedule && data.feeding_schedule.length > 0 ? (
            <div className="space-y-4">
              {data.feeding_schedule.map((meal: any, index: number) => {
                const isEditing = editingMeal === index;
                
                return (
                  <div key={index} className="border rounded-lg p-4">
                    {isEditing ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Orario *</Label>
                            <Input
                              type="time"
                              value={meal.time || ''}
                              onChange={(e) => updateMeal(index, 'time', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Quantità</Label>
                            <Input
                              value={meal.quantity || ''}
                              onChange={(e) => updateMeal(index, 'quantity', e.target.value)}
                              placeholder="es. 100g, 1 tazza"
                            />
                          </div>
                          <div>
                            <Label>Tipo Cibo</Label>
                            <Input
                              value={meal.food_type || ''}
                              onChange={(e) => updateMeal(index, 'food_type', e.target.value)}
                              placeholder="es. Crocchette, Umido"
                            />
                          </div>
                           <div>
                             <Label>Frequenza Piano Alimentare</Label>
                             <Select value={meal.frequency || 'daily'} onValueChange={(value) => updateMeal(index, 'frequency', value)}>
                               <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                                 <SelectValue placeholder="Seleziona frequenza" />
                               </SelectTrigger>
                               <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                                 <SelectItem value="daily">Giornaliero</SelectItem>
                                 <SelectItem value="weekly">Settimanale</SelectItem>
                                 <SelectItem value="monthly">Mensile</SelectItem>
                                 <SelectItem value="specific">Date Specifiche</SelectItem>
                               </SelectContent>
                             </Select>
                           </div>
                           
                           {meal.frequency === 'weekly' && (
                             <div className="md:col-span-2">
                               <Label>Giorni della Settimana</Label>
                               <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mt-2">
                                 {['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'].map((day, dayIndex) => (
                                   <label key={day} className="flex items-center gap-1 text-sm">
                                     <input
                                       type="checkbox"
                                       checked={(meal.days || []).includes(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex])}
                                       onChange={(e) => {
                                         const englishDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                         const currentDays = meal.days || [];
                                         const englishDay = englishDays[dayIndex];
                                         if (e.target.checked) {
                                           updateMeal(index, 'days', [...currentDays, englishDay]);
                                         } else {
                                           updateMeal(index, 'days', currentDays.filter((d: string) => d !== englishDay));
                                         }
                                       }}
                                     />
                                     {day.slice(0, 3)}
                                   </label>
                                 ))}
                               </div>
                             </div>
                           )}
                           
                           {meal.frequency === 'monthly' && (
                             <div>
                               <Label>Giorno del Mese</Label>
                               <Input
                                 type="number"
                                 min="1"
                                 max="31"
                                 value={meal.monthly_day || ''}
                                 onChange={(e) => updateMeal(index, 'monthly_day', e.target.value)}
                                 placeholder="es. 15 (ogni 15 del mese)"
                               />
                             </div>
                           )}
                           
                           {meal.frequency === 'specific' && (
                             <div className="md:col-span-2">
                               <Label>Date Specifiche</Label>
                               <div className="space-y-2 mt-2">
                                 {(meal.custom_dates || []).map((date: string, dateIndex: number) => (
                                   <div key={dateIndex} className="flex items-center gap-2">
                                     <Input
                                       type="date"
                                       value={date}
                                       onChange={(e) => {
                                         const newDates = [...(meal.custom_dates || [])];
                                         newDates[dateIndex] = e.target.value;
                                         updateMeal(index, 'custom_dates', newDates);
                                       }}
                                     />
                                     <Button
                                       type="button"
                                       variant="ghost"
                                       size="sm"
                                       onClick={() => {
                                         const newDates = meal.custom_dates || [];
                                         newDates.splice(dateIndex, 1);
                                         updateMeal(index, 'custom_dates', newDates);
                                       }}
                                     >
                                       <X className="w-4 h-4" />
                                     </Button>
                                   </div>
                                 ))}
                                 <Button
                                   type="button"
                                   variant="outline"
                                   size="sm"
                                   onClick={() => {
                                     const currentDates = meal.custom_dates || [];
                                     updateMeal(index, 'custom_dates', [...currentDates, '']);
                                   }}
                                 >
                                   <Plus className="w-4 h-4 mr-2" />
                                   Aggiungi Data
                                 </Button>
                               </div>
                             </div>
                           )}
                          <div className="md:col-span-2">
                            <Label>Note</Label>
                            <Textarea
                              value={meal.notes || ''}
                              onChange={(e) => updateMeal(index, 'notes', e.target.value)}
                              placeholder="Note sul pasto, ingredienti speciali..."
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => saveMeal(index)} size="sm">
                            <Check className="w-4 h-4 mr-2" />
                            Salva
                          </Button>
                          <Button variant="outline" onClick={() => removeMeal(index)} size="sm">
                            <X className="w-4 h-4 mr-2" />
                            Rimuovi
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium">
                              Pasto alle {meal.time || 'Orario non specificato'}
                            </h4>
                            {meal.date && (
                              <Badge variant="secondary">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(meal.date).toLocaleDateString('it-IT')}
                              </Badge>
                            )}
                            {meal.recurring !== false && !meal.date && (
                              <Badge variant="default">
                                Quotidiano
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            {meal.quantity && <p>Quantità: {meal.quantity}</p>}
                            {meal.food_type && <p>Tipo: {meal.food_type}</p>}
                            {meal.notes && <p>{meal.notes}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setEditingMeal(index)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeMeal(index)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Nessun pasto programmato. Clicca "Aggiungi Pasto" per iniziare.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Supplements */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Apple className="w-5 h-5 text-purple-500" />
            Integratori e Supplementi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="supplements">Integratori Attuali</Label>
            <Textarea
              id="supplements"
              value={data.supplements || ''}
              onChange={(e) => handleChange('supplements', e.target.value)}
              placeholder="Lista degli integratori utilizzati con dosaggi e frequenze..."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="supplement_start_date">Inizio Integrazione</Label>
              <Input
                id="supplement_start_date"
                type="date"
                value={data.supplement_start_date || ''}
                onChange={(e) => handleChange('supplement_start_date', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="supplement_end_date">Fine Integrazione</Label>
              <Input
                id="supplement_end_date"
                type="date"
                value={data.supplement_end_date || ''}
                onChange={(e) => handleChange('supplement_end_date', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weight Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            Gestione del Peso
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="target_weight">Peso Obiettivo (kg)</Label>
              <Input
                id="target_weight"
                type="number"
                step="0.1"
                value={data.target_weight || ''}
                onChange={(e) => handleChange('target_weight', e.target.value)}
                placeholder="Peso ideale"
              />
            </div>
            <div>
              <Label htmlFor="weight_goal_date">Data Obiettivo</Label>
              <Input
                id="weight_goal_date"
                type="date"
                value={data.weight_goal_date || ''}
                onChange={(e) => handleChange('weight_goal_date', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="next_weight_check">Prossimo Controllo Peso</Label>
              <Input
                id="next_weight_check"
                type="date"
                value={data.next_weight_check || ''}
                onChange={(e) => handleChange('next_weight_check', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="weight_notes">Note sul Peso</Label>
            <Textarea
              id="weight_notes"
              value={data.weight_notes || ''}
              onChange={(e) => handleChange('weight_notes', e.target.value)}
              placeholder="Obiettivi di peso, dieta speciale, consigli del veterinario..."
            />
          </div>
        </CardContent>
      </Card>

      {/* General Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Apple className="w-5 h-5 text-gray-500" />
            Note Alimentari Generali
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data.nutrition_notes || ''}
            onChange={(e) => handleChange('nutrition_notes', e.target.value)}
            placeholder="Note generali sull'alimentazione, preferenze, comportamenti alimentari..."
            rows={6}
          />
        </CardContent>
      </Card>
    </div>
  );
};